import React, { useState, useEffect } from 'react';
import { db } from '../db';
import { User, Plus, Lock, ArrowRight, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ClaimProfile from '../components/ClaimProfile';

const ProfileSelection = ({ onLogin }) => {
  const { currentUser } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClaim, setShowClaim] = useState(false);
  
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  
  const [showNewForm, setShowNewForm] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfilePin, setNewProfilePin] = useState('');
  const [newProfileConfirmPin, setNewProfileConfirmPin] = useState('');

  const [hasCheckedClaim, setHasCheckedClaim] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadProfiles();
    }
  }, [currentUser]);

  const loadProfiles = async () => {
    setLoading(true);
    const data = await db.getProfiles(currentUser?.uid);
    setProfiles(data);
    
    setHasCheckedClaim(true);
    setLoading(false);
  };

  const handleNumpadClick = (num) => {
    if (pinInput.length < 4) {
      setPinInput(prev => prev + num);
      setPinError(false);
    }
  };

  const handleBackspace = () => {
    setPinInput(prev => prev.slice(0, -1));
    setPinError(false);
  };

  useEffect(() => {
    if (pinInput.length === 4 && selectedProfile) {
      if (pinInput === selectedProfile.pin) {
        localStorage.setItem('fitTrackProfileId', selectedProfile.id);
        onLogin(selectedProfile);
      } else {
        setPinError(true);
        setTimeout(() => setPinInput(''), 800);
      }
    }
  }, [pinInput, selectedProfile, onLogin]);

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if (newProfileName && newProfilePin.length === 4) {
      const colors = ['#a89fcf', '#a3d9b1', '#e6b3cc', '#8c7fbf', '#7abf8d', '#d996b8'];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      
      const newProfile = await db.addProfile({
        name: newProfileName,
        pin: newProfilePin,
        color: randomColor
      }, currentUser?.uid);
      
      setShowNewForm(false);
      setNewProfileName('');
      setNewProfilePin('');
      setNewProfileConfirmPin('');
      loadProfiles();
      localStorage.setItem('fitTrackProfileId', newProfile.id);
      onLogin(newProfile); // Auto login
    }
  };

  if (loading) return <div style={{textAlign: 'center', marginTop: '20vh'}}>Chargement des profils...</div>;

  if (showClaim) {
    return <ClaimProfile onComplete={() => { setShowClaim(false); loadProfiles(); }} />;
  }

  return (
    <>
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'var(--auth-bg-gradient)',
        zIndex: -1,
        animation: 'fadeIn 0.5s ease-out'
      }}>
        <div style={{ position: 'absolute', top: '10%', left: '20%', width: '300px', height: '300px', background: 'var(--primary-color)', borderRadius: '50%', filter: 'blur(100px)', opacity: 'var(--blob-opacity)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '10%', right: '20%', width: '250px', height: '250px', background: 'var(--secondary-color)', borderRadius: '50%', filter: 'blur(100px)', opacity: 'var(--blob-opacity)', pointerEvents: 'none' }} />
      </div>
      
      <div style={{ maxWidth: '600px', margin: '10vh auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <h1 style={{ fontSize: '2.5rem', marginBottom: '2rem', color: 'var(--primary-color)' }}>Tes profils</h1>
      {/* PIN Modal */}
      {selectedProfile && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>Bienvenue, {selectedProfile.name}</h3>
          <p style={{ color: 'var(--text-secondary)' }}>Entre ton code PIN pour continuer</p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center', marginTop: '1rem' }}>
            {/* PIN Display */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              {[0, 1, 2, 3].map(i => (
                <div key={i} style={{ 
                  width: '20px', height: '20px', borderRadius: '50%', 
                  backgroundColor: pinInput.length > i ? 'var(--primary-color)' : 'var(--border-color)',
                  transition: 'background-color 0.2s'
                }} />
              ))}
            </div>
            
            {pinError && <p style={{ color: 'var(--danger-color)', margin: '0' }}>Code PIN incorrect</p>}

            {/* Numpad */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', width: '250px' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <button 
                  key={num} 
                  type="button" 
                  className="btn" 
                  style={{ height: '60px', fontSize: '1.5rem', backgroundColor: 'var(--bg-color)', color: 'var(--text-primary)' }}
                  onClick={() => handleNumpadClick(num.toString())}
                >
                  {num}
                </button>
              ))}
              <button 
                type="button" 
                className="btn" 
                style={{ height: '60px', fontSize: '1.5rem', backgroundColor: 'var(--bg-color)', color: 'var(--danger-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={handleBackspace}
              >
                ⌫
              </button>
              <button 
                type="button" 
                className="btn" 
                style={{ height: '60px', fontSize: '1.5rem', backgroundColor: 'var(--bg-color)', color: 'var(--text-primary)' }}
                onClick={() => handleNumpadClick('0')}
              >
                0
              </button>
              <button 
                type="button" 
                className="btn" 
                style={{ height: '60px', fontSize: '1.5rem', backgroundColor: 'transparent', color: 'var(--text-primary)' }}
                onClick={() => { setSelectedProfile(null); setPinInput(''); setPinError(false); }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile List */}
      {!selectedProfile && !showNewForm && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', justifyContent: 'center' }}>
          {profiles.map(profile => (
            <div 
              key={profile.id} 
              onClick={() => setSelectedProfile(profile)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', gap: '0.5rem' }}
            >
              <div style={{ 
                width: '100px', height: '100px', borderRadius: '16px', 
                backgroundColor: profile.color || 'var(--primary-color)', 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: 'var(--shadow)', transition: 'transform 0.2s ease',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {profile.avatarUrl ? (
                  <img loading="lazy" src={profile.avatarUrl} alt={profile.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <User size={48} color="#fff" />
                )}
              </div>
              <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{profile.name}</span>
            </div>
          ))}
          
          <div 
            onClick={() => setShowNewForm(true)}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', gap: '0.5rem' }}
          >
            <div style={{ 
              width: '100px', height: '100px', borderRadius: '16px', 
              border: '2px dashed var(--text-secondary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary-color)'; e.currentTarget.style.color = 'var(--primary-color)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--text-secondary)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
              <Plus size={48} />
            </div>
            <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>Ajouter</span>
          </div>
        </div>
      )}

      {/* New Profile Form */}
      {showNewForm && (
        <div className="card" style={{ padding: '2rem', maxWidth: '400px', margin: '0 auto' }}>
          <h2 style={{ marginBottom: '1.5rem', color: 'var(--primary-color)' }}>Nouveau Profil</h2>
          <form onSubmit={handleCreateProfile} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', textAlign: 'left' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Prénom ou Pseudo</label>
              <input 
                type="text" 
                className="form-control"
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
                placeholder="Ex: Alex"
                required
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Code PIN (4 chiffres)</label>
              <input 
                type="password" 
                className="form-control"
                value={newProfilePin}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setNewProfilePin(val);
                }}
                placeholder="----"
                maxLength="4"
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Confirmer le PIN</label>
              <input 
                type="password" 
                className="form-control"
                value={newProfileConfirmPin}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setNewProfileConfirmPin(val);
                }}
                placeholder="----"
                maxLength="4"
                required
              />
              {newProfilePin && newProfileConfirmPin && newProfilePin !== newProfileConfirmPin && (
                <p style={{ color: 'var(--danger-color)', fontSize: '0.8rem', marginTop: '0.5rem' }}>Les codes PIN ne correspondent pas.</p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button 
                type="button" 
                className="btn" 
                style={{ flex: 1, backgroundColor: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
                onClick={() => {
                  setShowNewForm(false);
                  setNewProfileName('');
                  setNewProfilePin('');
                  setNewProfileConfirmPin('');
                }}
              >
                Annuler
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                style={{ flex: 1 }}
                disabled={!newProfileName || newProfilePin.length !== 4 || newProfilePin !== newProfileConfirmPin}
              >
                Créer
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
    </>
  );
};

export default ProfileSelection;
